import React from 'react';
import { Button } from '@/components/ui/button';
import { Disclaimer } from '@/components/Disclaimer';
import {
  Camera, Target, TrendingUp, Sparkles, Shield,
  ChevronRight, Check, Star, CalendarDays, BarChart3, Pill, ShoppingCart,
  ChefHat, ArrowRight, Zap, Dumbbell, Heart, Scale, Scan, AlertTriangle,
  Upload, Lock, Smile, Activity, BookOpen, Utensils, Refrigerator,
  Globe, Users, PlayCircle, Brain, HeartHandshake, Baby, Leaf, Flame, Cookie
} from 'lucide-react';
import {
  PhoneMockup, LogMealMockupContent, FollowUpMockupContent,
  FoodDetailMockupContent, CoachMockupContent, OptimizeMockupContent, RecipeMockupContent,
  FridgeMockupContent, GLP1MockupContent
} from '@/components/PhoneMockup';
import { C } from './LandingConstants';

const SectionTag = ({ children, color }) => (
  <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: color || C.muted, letterSpacing: '0.8px' }}>{children}</span>
);

function CalorieCompare({ label, visible, hidden, total, isGood }) {
  const visiblePct = (visible / total) * 100;
  const hiddenPct = (hidden / total) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium" style={{ color: '#1A1A18' }}>{label}</span>
        <span className="text-sm font-bold" style={{ color: isGood ? '#5A8C6A' : '#C67A5C' }}>{total} cal</span>
      </div>
      <div className="h-3 rounded-full overflow-hidden flex" style={{ background: '#E8E3DB' }}>
        <div className="h-full rounded-l-full" style={{ width: `${visiblePct}%`, background: '#5A8C6A' }} />
        {hiddenPct > 0 && <div className="h-full" style={{ width: `${hiddenPct}%`, background: '#C67A5C' }} />}
      </div>
      <div className="flex items-center gap-4 text-xs" style={{ color: '#9A9590' }}>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#5A8C6A]" /> Tracked: {visible}</span>
        {hidden > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#C67A5C]" /> Missed: +{hidden}</span>}
      </div>
    </div>
  );
}

export function StatsBar() {
  return (
    <section className="py-10 px-6" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.cream }}>
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { value: '1,050+', label: 'Curated recipes across 12 health modes', color: C.accent },
          { value: '900+', label: 'Restaurant chains with verified nutrition', color: C.green },
          { value: '<3s', label: 'AI analysis time', color: C.gold },
          { value: '12', label: 'Specialized health modes', color: C.green },
        ].map((stat, idx) => (
          <div key={stat.value} data-testid={`stat-${idx}`}>
            <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: stat.color, fontFamily: 'var(--font-sans)' }}>{stat.value}</div>
            <div className="text-xs" style={{ color: C.muted }}>{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <SectionTag color={C.green}>How It Works</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>Three steps to the full picture</h2>
          <p className="text-base max-w-md mx-auto" style={{ color: C.muted }}>No manual searching. No guessing portion sizes. Just point, shoot, and know.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: '01', title: 'Snap Your Meal', desc: 'Take a photo. Nuri AI Vision identifies every item on your plate in under 3 seconds.', color: C.accent, mockup: <LogMealMockupContent /> },
            { step: '02', title: 'Answer Smart Follow-Ups', desc: '"Was that oil or butter? Did you finish the dressing?" We ask what others don\'t.', color: C.green, mockup: <FollowUpMockupContent /> },
            { step: '03', title: 'See the Full Picture', desc: 'Accurate calories, macros, health score, and AI coaching — all from one photo.', color: C.gold, mockup: <FoodDetailMockupContent /> },
          ].map((item, idx) => (
            <div key={item.step} data-testid={`how-step-${idx}`} className="text-center">
              <div className="flex justify-center mb-5">
                <PhoneMockup className="scale-90">{item.mockup}</PhoneMockup>
              </div>
              <div className="text-xs font-bold mb-2" style={{ color: item.color }}>STEP {item.step}</div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: C.text }}>{item.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AccuracySection() {
  return (
    <section id="full-picture" className="py-20 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <SectionTag color={C.accent}>Accuracy</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
            Most AI trackers stop at the photo.<br /><span style={{ color: C.green }}>We go further.</span>
          </h2>
          <p className="text-base max-w-2xl mx-auto" style={{ color: C.textSec }}>
            Other apps snap a photo and guess. Nuri snaps a photo and <em>asks</em>. Was there dressing? How was it cooked? Those two questions are the difference between 280 and 485 calories.
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          <div className="text-center">
            <p className="text-sm font-semibold mb-4" style={{ color: '#C45B5B' }}>What photo-only apps see</p>
            <PhoneMockup className="scale-90 md:scale-100">
              <div className="w-full h-full p-3 pt-8 text-[8px]" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
                <div className="rounded-xl p-3" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
                  <p className="text-[10px] font-medium mb-1" style={{ color: '#1A1A18' }}>Chicken Caesar Salad</p>
                  <div className="grid grid-cols-4 gap-1 text-center mb-2">
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>280</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Calories</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>32g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Protein</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>8g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Carbs</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>12g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Fat</p></div>
                  </div>
                  <p className="text-[6px] italic" style={{ color: '#9A9590' }}>No follow-up questions asked</p>
                </div>
                <div className="mt-3 space-y-2">
                  <CalorieCompare label="Chicken Caesar" visible={280} hidden={0} total={280} isGood={false} />
                </div>
              </div>
            </PhoneMockup>
          </div>
          <div className="hidden md:flex flex-col items-center gap-2">
            <ArrowRight className="w-6 h-6" style={{ color: C.green }} />
            <span className="text-[10px] font-semibold text-center" style={{ color: C.green }}>+205 cal<br />found</span>
          </div>
          <div className="md:hidden text-center py-2">
            <span className="text-xs font-semibold" style={{ color: C.green }}>+205 hidden calories found &#8595;</span>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold mb-4" style={{ color: C.green }}>What Nuri catches</p>
            <PhoneMockup className="scale-90 md:scale-100">
              <div className="w-full h-full p-3 pt-8 text-[8px]" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
                <div className="rounded-xl p-3" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-medium" style={{ color: '#1A1A18' }}>Chicken Caesar Salad</p>
                    <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#FFF8ED', color: '#D4880F' }}>C+</span>
                  </div>
                  <span className="text-[6px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: '#C67A5C15', color: '#C67A5C' }}>+205 cal from follow-ups</span>
                  <div className="grid grid-cols-4 gap-1 text-center mt-2">
                    <div><p className="text-[9px] font-medium" style={{ color: '#C67A5C' }}>485</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Calories</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>38g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Protein</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>24g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Carbs</p></div>
                    <div><p className="text-[9px] font-medium" style={{ color: '#1A1A18' }}>22g</p><p className="text-[5px]" style={{ color: '#9A9590' }}>Fat</p></div>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <CalorieCompare label="Chicken Caesar" visible={280} hidden={205} total={485} isGood={true} />
                </div>
                <div className="mt-2 text-[6px]" style={{ color: '#9A9590' }}>
                  <p>Ranch dressing: +180 cal</p>
                  <p>Croutons: +25 cal</p>
                </div>
              </div>
            </PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CoachSection({ setShowAuth }) {
  const prompts = [
    { category: 'Meal Building', color: C.accent, items: [
      { text: 'Build me a dinner with 40g protein under 500 calories' },
      { text: "I'm eating at Chipotle — what should I order?" },
      { text: 'What can I make with chicken, rice, and broccoli?' },
    ]},
    { category: 'Daily Coaching', color: C.green, items: [
      { text: "I'm 20g short on protein — what's a quick snack?" },
      { text: 'Optimize my day — tweak my meals to hit my targets' },
      { text: "How's my nutrition this week?" },
    ]},
    { category: 'Quick Logging', color: C.gold, items: [
      { text: 'Log my breakfast — 2 eggs and toast with butter' },
      { text: 'I just ran 3 miles in 25 minutes' },
    ]},
  ];
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: C.greenBg, border: `1px solid ${C.green}30` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: C.green }} />
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.green }}>AI Coach</span>
          </div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
            A personal nutritionist that knows your <span style={{ color: C.green }}>complete data</span>
          </h2>
          <p className="text-base max-w-lg mx-auto mt-4" style={{ color: C.textSec }}>
            Your AI Coach sees every meal, every macro gap, every workout. Ask it anything — or let it proactively suggest your next move.
          </p>
        </div>
        <div className="flex flex-col lg:flex-row items-center gap-10">
          <div className="lg:flex-1 w-full space-y-6">
            {prompts.map(group => (
              <div key={group.category}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: group.color }}>{group.category}</p>
                <div className="space-y-2">
                  {group.items.map((item, idx) => (
                    <div key={item.text.slice(0, 20)} className="flex-1 rounded-2xl rounded-br-sm px-4 py-2.5 text-sm font-medium leading-snug"
                      style={{ background: C.cream, color: C.text, border: `1px solid ${C.border}` }}>
                      {item.text}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={() => setShowAuth(true)} className="w-full h-11 rounded-xl font-semibold mt-3 text-white" data-testid="coach-try-btn"
              style={{ background: C.green, border: 'none' }}>
              Try AI Coach Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="lg:flex-shrink-0">
            <PhoneMockup angle><CoachMockupContent variant="build" /></PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

export function OptimizeSection() {
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:flex-1">
            <SectionTag color={C.gold}>Smart Optimization</SectionTag>
            <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
              Same meals. Small tweaks.<br /><span style={{ color: C.green }}>Hit your targets.</span>
            </h2>
            <p className="text-base leading-relaxed mb-6 max-w-lg" style={{ color: C.textSec }}>
              Nuri analyzes what you actually ate and shows you how to hit every target tomorrow with minimal changes.
            </p>
            <div className="space-y-3 mb-6">
              {[
                { from: 'Whole milk in coffee', to: 'Greek yogurt parfait', impact: '+12g protein, +20 cal' },
                { from: 'Potato chips at lunch', to: 'Side salad w/ vinaigrette', impact: '-150 cal' },
                { from: 'White rice at dinner', to: 'Quinoa pilaf', impact: '+6g protein, -30 cal' },
              ].map((swap) => (
                <div key={swap.from} className="p-3 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs line-through" style={{ color: '#C45B5B' }}>{swap.from}</span>
                    <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color: C.muted }} />
                    <span className="text-xs font-semibold" style={{ color: C.green }}>{swap.to}</span>
                  </div>
                  <span className="text-[10px]" style={{ color: C.gold }}>{swap.impact}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:flex-shrink-0">
            <PhoneMockup angle><OptimizeMockupContent /></PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FeaturesGrid() {
  const heroFeatures = [
    { title: 'AI Photo Analysis + Follow-Ups', desc: 'Snap a photo. Nuri AI Vision identifies every item, then asks 2 smart follow-up questions to catch the 200-500 hidden calories.', color: C.accent, mockup: <FoodDetailMockupContent />, reverse: false, icon: Camera },
    { title: 'AI Coach \u2014 "Build My Next Meal"', desc: 'Nuri AI Coach sees every meal and macro gap. Ask it to build a meal that fits your remaining budget.', color: C.green, mockup: <CoachMockupContent variant="build" />, reverse: true, icon: Sparkles },
    { title: 'Custom Recipe Builder', desc: "Create \"Mom's Chicken Enchiladas\" with ingredient-level macro calculation. Save, one-tap log, and auto-add to your grocery list.", color: C.gold, mockup: <RecipeMockupContent />, reverse: false, icon: ChefHat },
    { title: 'Fridge / Pantry Scanner', desc: '"What can I make with what\'s in my fridge?" Scan your ingredients and get AI-generated meal suggestions with macros.', color: C.green, mockup: <FridgeMockupContent />, reverse: true, icon: Refrigerator },
  ];
  const gridFeatures = [
    { icon: Target, title: 'Goal-Based TDEE Targets', desc: 'Mifflin-St Jeor calculation with targets for weight loss, muscle gain, or maintenance.', color: C.accent },
    { icon: HeartHandshake, title: 'Gentle Mode', desc: 'Compassionate tracking without calorie numbers. Focus on balance, nourishment, and building a healthy relationship with food.', color: C.green },
    { icon: Globe, title: 'Regional Cuisine', desc: 'South Asian, Mediterranean, Latin American, East Asian, Middle Eastern, and African recipes — all macro-tagged.', color: C.gold },
    { icon: BookOpen, title: '1,050+ Recipe Library', desc: 'Curated recipes spanning 12 health modes with regional variety, chef tips, and one-tap logging.', color: C.accent },
    { icon: PlayCircle, title: 'Cooking Mode', desc: 'Step-by-step guided cooking with hands-free voice navigation, timers, and auto-logging when done.', color: C.green },
    { icon: Brain, title: 'Smart Food Memory', desc: 'Nuri remembers your favorites, portion sizes, and cooking preferences. Logging gets faster every day.', color: C.gold },
    { icon: Users, title: 'Recipe Sharing Community', desc: 'Share your custom recipes, discover what others are cooking, and save community favorites.', color: C.green },
    { icon: Dumbbell, title: 'Exercise Intelligence + Recovery', desc: 'Net calorie tracking and 90-min post-workout recovery window with protein-first suggestions.', color: C.accent },
    { icon: Scale, title: 'Scale-Aware Photo Logging', desc: 'AI reads your kitchen scale display for exact gram-weight accuracy.', color: C.gold },
    { icon: AlertTriangle, title: 'Allergen Detection', desc: 'Flags peanuts, gluten, dairy, shellfish, and 12+ dietary restrictions.', color: C.accent },
    { icon: BarChart3, title: 'Health Score (A-F)', desc: 'Every meal gets a letter grade based on nutritional balance.', color: C.green },
    { icon: Activity, title: 'Micronutrient Tracking', desc: 'Go beyond macros — track fiber, sodium, vitamins, and minerals.', color: C.green },
    { icon: CalendarDays, title: 'Meal Planner + Grocery List', desc: 'Weekly planning with auto-generated, categorized shopping lists.', color: C.gold },
    { icon: ShoppingCart, title: 'Batch Upload "Log My Whole Day"', desc: 'Forgot to log? Upload all photos at once for a full daily report.', color: C.green },
    { icon: Smile, title: 'Mood & Energy Tracking', desc: 'Tag mood after meals. Discover food-mood correlations over time.', color: C.gold },
    { icon: TrendingUp, title: 'Weight Tracking & Trends', desc: 'Visual trend graph with weekly deficit tracking and goal progress.', color: C.accent },
    { icon: Scan, title: 'Barcode Scanner', desc: 'Scan packaged food for instant nutrition from major databases.', color: C.muted },
    { icon: Utensils, title: 'Restaurant Database (900+)', desc: 'USDA-verified data + verified nutrition from 900+ chains.', color: C.green },
  ];
  return (
    <section id="features" className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <SectionTag color={C.gold}>Features</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
            Everything you need to eat for performance
          </h2>
        </div>
        <div className="space-y-16 mb-16">
          {heroFeatures.map((feat, idx) => (
            <div key={feat.title} className={`flex flex-col ${feat.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-12`} data-testid={`hero-feature-${idx}`}>
              <div className="lg:flex-1 max-w-md">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: `${feat.color}15` }}>
                  <feat.icon className="w-5 h-5" style={{ color: feat.color }} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: C.text }}>{feat.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.textSec }}>{feat.desc}</p>
              </div>
              <div className="lg:flex-shrink-0">
                <PhoneMockup angle={feat.reverse} className="scale-90 lg:scale-100">{feat.mockup}</PhoneMockup>
              </div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {gridFeatures.map((feat) => (
            <div key={feat.title} className="rounded-xl p-5 nuri-card-hover" data-testid={`feature-card-${feat.title.slice(0,10)}`}
              style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${feat.color}12` }}>
                <feat.icon className="w-5 h-5" style={{ color: feat.color }} />
              </div>
              <h3 className="font-semibold text-sm mb-1" style={{ color: C.text }}>{feat.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: C.muted }}>{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PrivacySection() {
  return (
    <section className="py-16 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-5xl mx-auto">
        <div className="rounded-2xl p-8 md:p-12" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: C.greenBg, border: `1px solid ${C.green}30` }}>
                <Lock className="w-3.5 h-3.5" style={{ color: C.green }} />
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.green }}>Privacy First</span>
              </div>
              <h2 className="text-2xl md:text-3xl mb-4" style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, color: C.text }}>Your data is yours. Period.</h2>
              <p className="text-sm leading-relaxed mb-4" style={{ color: C.textSec }}>
                We never sell your data or use advertising SDKs. Meal photos are processed by AI for nutritional analysis only.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Shield, label: 'AES-256 Encrypted', desc: 'Data encrypted at rest and in transit' },
                { icon: Lock, label: 'No Data Sold, Ever', desc: 'Zero advertisers, zero third parties' },
                { icon: Upload, label: 'Full Data Export', desc: 'Download everything, anytime' },
                { icon: ArrowRight, label: 'GDPR Compliant', desc: 'Delete all your data in one tap' },
              ].map((item) => (
                <div key={item.label} className="p-3 rounded-xl" style={{ background: C.cream, border: `1px solid ${C.borderLight}` }}>
                  <item.icon className="w-5 h-5 mb-2" style={{ color: C.green }} />
                  <p className="text-xs font-semibold" style={{ color: C.text }}>{item.label}</p>
                  <p className="text-[10px]" style={{ color: C.muted }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function GLP1Section() {
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: '#9B59B612', border: '1px solid #9B59B630' }}>
              <Pill className="w-3.5 h-3.5" style={{ color: '#9B59B6' }} />
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#9B59B6' }}>GLP-1 Ready</span>
            </div>
            <h2 className="mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
              Built for the <span style={{ color: '#9B59B6' }}>GLP-1 generation</span>
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: C.textSec }}>
              Whether you're on Ozempic, Wegovy, Mounjaro, or Zepbound — Nuri adapts to your unique nutritional needs.
            </p>
            <ul className="space-y-3">
              {[
                'Protein-first meal suggestions to prevent muscle loss',
                'Injection tracking with site rotation reminders',
                'Side effect logging and pattern detection',
                'GLP-1-aware AI coaching with dose-specific guidance',
              ].map((item) => (
                <li key={item.slice(0, 15)} className="flex items-start gap-3 text-sm" style={{ color: C.textSec }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#9B59B615' }}>
                    <Check className="w-3 h-3" style={{ color: '#9B59B6' }} />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:flex-shrink-0">
            <PhoneMockup angle><GLP1MockupContent /></PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HealthModesSection() {
  const modes = [
    { name: 'Weight Loss', icon: Scale, color: C.accent, desc: 'Calorie deficit with protein protection' },
    { name: 'Muscle Gain', icon: Dumbbell, color: C.green, desc: 'Surplus calories, high protein targets' },
    { name: 'Heart Health', icon: Heart, color: '#E85D75', desc: 'Low sodium, heart-friendly fats' },
    { name: 'Diabetes', icon: Activity, color: C.gold, desc: 'Glycemic-aware meal planning' },
    { name: 'GLP-1 Support', icon: Pill, color: '#9B59B6', desc: 'Protein-first, muscle preservation' },
    { name: 'Pregnancy', icon: Baby, color: '#E88DA0', desc: 'Folate, iron, DHA-focused nutrition' },
    { name: 'Keto', icon: Flame, color: '#E8785A', desc: 'Ultra-low carb, high fat tracking' },
    { name: 'Vegan / Plant-Based', icon: Leaf, color: C.green, desc: 'Complete protein combinations' },
    { name: 'Celiac / Gluten-Free', icon: Cookie, color: C.gold, desc: 'Gluten detection + safe alternatives' },
    { name: 'ED Recovery', icon: HeartHandshake, color: '#7CB8A0', desc: 'Gentle, number-free nourishment' },
    { name: 'Low-FODMAP', icon: AlertTriangle, color: C.accent, desc: 'IBS-safe food filtering' },
    { name: 'Senior Nutrition', icon: Users, color: C.green, desc: 'Bone health, B12, hydration focus' },
  ];
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <SectionTag color={C.green}>12 Health Modes</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
            One app. <span style={{ color: C.green }}>Your</span> health mode.
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: C.textSec }}>
            Every recipe, every suggestion, every coaching tip adapts to your specific health goal — with 1,050+ recipes tagged and ready.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {modes.map((mode) => (
            <div key={mode.name} className="rounded-xl p-4 nuri-card-hover" data-testid={`health-mode-${mode.name.slice(0,8)}`}
              style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ background: `${mode.color}12` }}>
                <mode.icon className="w-4.5 h-4.5" style={{ color: mode.color }} />
              </div>
              <p className="text-sm font-semibold mb-0.5" style={{ color: C.text }}>{mode.name}</p>
              <p className="text-[11px] leading-relaxed" style={{ color: C.muted }}>{mode.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function GentleModeSection() {
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: '#7CB8A012', border: '1px solid #7CB8A030' }}>
              <HeartHandshake className="w-3.5 h-3.5" style={{ color: '#7CB8A0' }} />
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#7CB8A0' }}>Gentle Mode</span>
            </div>
            <h2 className="mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
              Tracking without the <span style={{ color: '#7CB8A0' }}>anxiety</span>
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: C.textSec }}>
              Gentle Mode hides calorie numbers and focuses on food quality, balance, and nourishment. 
              Built for ED recovery, intuitive eating, and anyone who wants nutrition awareness without the stress.
            </p>
            <ul className="space-y-3">
              {[
                'No calorie counts — focus on food groups and balance',
                'Encouraging, non-judgmental AI coaching language',
                'Nourishment scores instead of letter grades',
                'Mindful eating prompts and mood-food journaling',
              ].map((item) => (
                <li key={item.slice(0, 15)} className="flex items-start gap-3 text-sm" style={{ color: C.textSec }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#7CB8A015' }}>
                    <Check className="w-3 h-3" style={{ color: '#7CB8A0' }} />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:flex-shrink-0">
            <PhoneMockup angle>
              <div className="w-full h-full p-3 pt-8 text-[8px] overflow-hidden" style={{ background: '#FDFBF7', color: '#1A1A18' }}>
                <div className="flex items-center gap-1.5 mb-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#EBF3EE' }}>
                    <span className="text-[8px]">&#127807;</span>
                  </div>
                  <span className="text-[8px] font-medium" style={{ color: '#1A1A18' }}>Gentle Dashboard</span>
                </div>
                <div className="rounded-xl p-2.5 mb-2" style={{ background: '#EBF3EE', border: '1px solid #5A8C6A30' }}>
                  <p className="text-[7px] font-medium mb-1" style={{ color: '#5A8C6A' }}>Today's Nourishment</p>
                  <div className="flex gap-2">
                    {['Protein', 'Veggies', 'Grains', 'Fruits'].map((g, i) => (
                      <div key={g} className="flex-1 text-center">
                        <div className="w-full h-1.5 rounded-full mb-0.5" style={{ background: '#E8E3DB' }}>
                          <div className="h-full rounded-full" style={{ width: `${[75, 60, 90, 40][i]}%`, background: '#5A8C6A' }} />
                        </div>
                        <p className="text-[5px]" style={{ color: '#9A9590' }}>{g}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl p-2.5 mb-2" style={{ background: '#FFFFFF', border: '1px solid #E8E3DB' }}>
                  <p className="text-[7px] font-medium mb-1" style={{ color: '#1A1A18' }}>Grilled salmon with quinoa</p>
                  <p className="text-[6px]" style={{ color: '#5A8C6A' }}>Balanced meal — great protein and whole grains</p>
                  <p className="text-[5px] mt-0.5" style={{ color: '#9A9590' }}>No numbers. Just nourishment.</p>
                </div>
                <div className="rounded-xl p-2 mb-2" style={{ background: '#FFF8ED', border: '1px solid #C6A05C30' }}>
                  <p className="text-[6px] font-medium" style={{ color: '#C6A05C' }}>Mindful moment</p>
                  <p className="text-[5px]" style={{ color: '#6A6560' }}>How did that meal make you feel? Take a breath and check in.</p>
                </div>
              </div>
            </PhoneMockup>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RegionalCuisineSection() {
  const regions = [
    { name: 'South Asian', flag: '\u{1F1EE}\u{1F1F3}', dishes: 'Dal, biryani, roti, paneer', count: '120+' },
    { name: 'Mediterranean', flag: '\u{1F1EC}\u{1F1F7}', dishes: 'Hummus, falafel, tabbouleh', count: '95+' },
    { name: 'Latin American', flag: '\u{1F1F2}\u{1F1FD}', dishes: 'Tacos, arroz con pollo, ceviche', count: '85+' },
    { name: 'East Asian', flag: '\u{1F1EF}\u{1F1F5}', dishes: 'Stir-fry, sushi, pho, congee', count: '110+' },
    { name: 'Middle Eastern', flag: '\u{1F1F1}\u{1F1E7}', dishes: 'Shawarma, kebab, fattoush', count: '75+' },
    { name: 'African', flag: '\u{1F1F3}\u{1F1EC}', dishes: 'Jollof rice, injera, tagine', count: '60+' },
  ];
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <SectionTag color={C.gold}>Regional Cuisine</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
            Your culture. <span style={{ color: C.gold }}>Your food.</span> Fully tracked.
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: C.textSec }}>
            Most trackers only know "chicken and rice." Nuri understands biryani, jollof rice, pho, and 1,050+ dishes from kitchens around the world.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {regions.map((r) => (
            <div key={r.name} className="rounded-xl p-5 nuri-card-hover" data-testid={`region-${r.name.slice(0,6)}`}
              style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg">{r.flag}</span>
                <span className="text-xs font-bold" style={{ color: C.accent }}>{r.count}</span>
              </div>
              <p className="text-sm font-semibold mb-1" style={{ color: C.text }}>{r.name}</p>
              <p className="text-xs" style={{ color: C.muted }}>{r.dishes}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



export function MFPSection() {
  return (
    <section className="py-16 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl mb-4" style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, color: C.text }}>
          Switching from MyFitnessPal?<br /><span style={{ color: C.green }}>Bring your history with you.</span>
        </h2>
        <p className="text-base mb-10 max-w-lg mx-auto" style={{ color: C.textSec }}>
          Import your food log in 60 seconds. Your AI Coach starts coaching from day one.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <span className="text-lg font-bold" style={{ color: C.muted }}>MFP</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-0.5 hidden sm:block" style={{ background: C.border }} />
            <div className="px-3 py-1.5 rounded-full" style={{ background: C.greenBg, border: `1px solid ${C.green}30` }}>
              <span className="text-xs font-semibold" style={{ color: C.green }}>CSV Import</span>
            </div>
            <div className="w-12 h-0.5 hidden sm:block" style={{ background: C.border }} />
          </div>
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: C.greenBg, border: `1px solid ${C.green}30` }}>
            <img src="/nuri-logo.svg" alt="nuri" style={{ height: 40 }} />
          </div>
        </div>
        <p className="text-sm font-medium mt-6" style={{ color: C.green }}>847 meals imported in under 60 seconds</p>
      </div>
    </section>
  );
}

export function DatabaseSection() {
  return (
    <section className="py-16 px-6 md:px-12" style={{ background: C.bg }} data-testid="database-credibility-section">
      <div className="max-w-4xl mx-auto text-center">
        <SectionTag color={C.gold}>Trusted Data</SectionTag>
        <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontWeight: 400, color: C.text }}>
          Every calorie backed by <span style={{ color: C.green }}>real data</span>
        </h2>
        <p className="text-base max-w-2xl mx-auto mb-10" style={{ color: C.textSec }}>
          Nuri combines USDA-verified food data, verified nutrition from 900+ chains, 3M+ packaged products, and 1,050+ curated recipes.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '1,050+', label: 'Curated recipes across 12 health modes', color: C.accent },
            { value: '900+', label: 'Restaurant chains with verified menus', color: C.green },
            { value: '3M+', label: 'Packaged products from Open Food Facts', color: C.gold },
            { value: 'Nuri AI', label: 'AI estimation when data is unavailable', color: C.gold },
          ].map((s) => (
            <div key={s.value} className="rounded-xl p-4" style={{ background: `${s.color}08`, border: `1px solid ${C.border}` }}>
              <p className="text-xl font-bold mb-1" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[11px] leading-relaxed" style={{ color: C.muted }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingSection({ setShowAuth }) {
  return (
    <section id="pricing" className="py-20 px-6 md:px-12" style={{ background: C.cream }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <SectionTag color={C.gold}>Pricing</SectionTag>
          <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>Simple, transparent pricing</h2>
          <p className="text-base" style={{ color: C.muted }}>Start free. Upgrade when you're ready.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {[
            { name: 'Free', price: '$0', sub: 'Get started, no card needed', border: C.border, borderW: 1, badge: null, btnStyle: { background: C.cream, border: `1px solid ${C.border}`, color: C.text }, btnText: 'Start Free', testId: 'free-plan-btn', features: ['3 meals/day', 'Basic calorie tracking', '5 AI Coach messages/week', 'Weight logging', 'GLP-1 tracker'] },
            { name: 'Pro', price: '$14.99', sub: 'or $119.99/year (save 33%)', border: C.accent, borderW: 2, badge: 'Most Popular', badgeColor: C.accent, btnStyle: { background: C.accent, border: 'none', color: 'white' }, btnText: 'Start Pro', testId: 'pro-plan-btn', features: ['Everything in Free', 'Unlimited meal logging', 'Smart follow-up questions (+26% accuracy)', '50 photo scans / 100 coach messages', '1,050+ recipes across 12 health modes', 'Gentle Mode & ED Recovery support', 'Regional cuisine (6 regions)', 'Recipe sharing community', 'Cooking Mode with voice navigation', 'Smart Food Memory', 'Post-workout recovery window', 'Fridge/Pantry Scanner', 'Health Score (A-F) on every meal', 'Allergen detection & flags', 'Grocery list from meal plans', 'MyFitnessPal data import'] },
            { name: 'Pro+', price: '$19.99', sub: 'or $159.99/year (save 33%)', border: C.green, borderW: 2, badge: 'Unlimited AI', badgeColor: C.green, btnStyle: { background: C.green, border: 'none', color: 'white' }, btnText: 'Go Unlimited', testId: 'pro-plus-plan-btn', features: ['Everything in Pro', 'Unlimited photo scans', 'Unlimited AI Coach messages', 'Unlimited text & voice logging', 'Priority AI processing', 'Advanced meal pattern analysis', 'AI-powered grocery optimizer', 'Sleep-aware coaching'] },
            { name: 'Family', price: '$34.99', sub: '4 members, one plan', border: C.border, borderW: 1, badge: null, btnStyle: { background: C.cream, border: `1px solid ${C.border}`, color: C.muted }, btnText: 'Coming Soon', testId: 'family-plan-btn', disabled: true, features: ['Everything in Pro (x4)', 'Family meal planner', 'Shared grocery list', 'Individual macro targets', 'Unified billing'] },
          ].map(plan => (
            <div key={plan.name} className="rounded-2xl p-6 relative" style={{ background: C.card, border: `${plan.borderW}px solid ${plan.border}` }}>
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: plan.badgeColor }}>{plan.badge}</div>}
              <h3 className="text-lg font-semibold mb-1" style={{ color: C.text }}>{plan.name}</h3>
              <div className="text-3xl font-bold mb-1" style={{ color: plan.badge ? plan.badgeColor : C.text }}>{plan.price}<span className="text-sm font-normal" style={{ color: C.muted }}>/mo</span></div>
              <p className="text-sm mb-6" style={{ color: C.muted }}>{plan.sub}</p>
              <ul className="space-y-2.5 mb-8">
                {plan.features.map((f) => (
                  <li key={f.slice(0, 20)} className="flex items-center gap-2 text-sm" style={{ color: C.textSec }}>
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: C.green }} />{f}
                  </li>
                ))}
              </ul>
              {plan.disabled ? (
                <>
                  <p className="text-xs text-center mb-3" style={{ color: `${C.accent}80` }}>Coming soon</p>
                  <Button disabled className="w-full h-11 rounded-xl cursor-not-allowed" data-testid={plan.testId} style={plan.btnStyle}>{plan.btnText}</Button>
                  <p className="text-xs text-center mt-2" style={{ color: C.muted }}>Just $8.75/person</p>
                </>
              ) : (
                <Button onClick={() => setShowAuth(true)} className="w-full h-11 rounded-xl" data-testid={plan.testId} style={plan.btnStyle}>{plan.btnText}</Button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-10 text-center max-w-2xl mx-auto">
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            Noom charges $59/month. MyFitnessPal charges $80-$100/year for basic tracking. Nuri gives you AI coaching, recipes, grocery lists, allergen detection, exercise intelligence, and 900+ restaurant menus starting at $14.99/month.
          </p>
        </div>
      </div>
    </section>
  );
}

export function BetaCTA({ setShowAuth }) {
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-3xl mx-auto text-center">
        <SectionTag color={C.gold}>Early Access</SectionTag>
        <h2 className="mt-3 mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>Join the Beta</h2>
        <p className="text-base mb-6 max-w-lg mx-auto" style={{ color: C.textSec }}>
          We're inviting the first 500 users to try Nuri free.
        </p>
        <div className="rounded-2xl p-6 max-w-md mx-auto mb-8" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <p className="text-sm font-medium mb-4" style={{ color: C.textSec }}>Photo-only trackers vs Nuri with follow-ups</p>
          <div className="flex items-center justify-center gap-6 mb-3">
            <div><p className="text-2xl font-bold" style={{ color: C.muted }}>~65%</p><p className="text-[10px]" style={{ color: C.muted }}>Photo-only</p></div>
            <ArrowRight className="w-5 h-5" style={{ color: C.green }} />
            <div><p className="text-2xl font-bold" style={{ color: C.green }}>~90%</p><p className="text-[10px]" style={{ color: C.green }}>With Nuri</p></div>
          </div>
          <p className="text-xs" style={{ color: C.muted }}>Follow-up questions caught an average of <strong style={{ color: C.accent }}>205 hidden calories</strong> per meal</p>
        </div>
        <Button onClick={() => setShowAuth(true)} className="h-12 px-8 text-base font-semibold rounded-xl text-white" data-testid="beta-cta-btn"
          style={{ background: C.accent, border: 'none' }}>
          Get Early Access <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </section>
  );
}

export function FinalCTA({ setShowAuth }) {
  return (
    <section className="py-20 px-6 md:px-12" style={{ background: C.bg }}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="mb-4" style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 400, color: C.text }}>
          Stop guessing.<br />Start <span style={{ color: C.green }}>performing</span>.
        </h2>
        <p className="text-base mb-8" style={{ color: C.muted }}>
          Join the athletes and fitness enthusiasts who finally see the full picture of their nutrition.
        </p>
        <Button onClick={() => setShowAuth(true)} className="h-14 px-10 text-lg font-semibold rounded-xl text-white nuri-btn-press mb-8" data-testid="final-cta-btn"
          style={{ background: C.accent, border: 'none' }}>
          Start Tracking Free <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <div className="flex justify-center">
          <PhoneMockup className="scale-90"><CoachMockupContent variant="build" /></PhoneMockup>
        </div>
      </div>
    </section>
  );
}

const NATURE_THEMES = [
  { name: 'Mountain Morning', gradient: 'linear-gradient(135deg, #E8EAD3 0%, #C4D4B0 40%, #8FA98A 100%)', textColor: '#3D5A3E', accentColor: '#5A8C6A', illustration: 'M0,40 Q200,0 400,30 T800,20 L800,80 L0,80 Z' },
  { name: 'Ocean Tide', gradient: 'linear-gradient(135deg, #D4E5F0 0%, #A8C8DB 40%, #7BA8C4 100%)', textColor: '#2C4A5A', accentColor: '#4A90D9', illustration: 'M0,50 C100,30 200,55 300,35 S500,55 600,30 S800,50 800,50 L800,80 L0,80 Z' },
  { name: 'Desert Dawn', gradient: 'linear-gradient(135deg, #F5E6D3 0%, #E8CEB0 40%, #D4A574 100%)', textColor: '#6B4A30', accentColor: '#C67A5C', illustration: 'M0,60 Q100,40 200,55 T400,45 T600,50 T800,55 L800,80 L0,80 Z' },
  { name: 'Autumn Harvest', gradient: 'linear-gradient(135deg, #F0E2C8 0%, #E5C896 40%, #C69F5A 100%)', textColor: '#5A4520', accentColor: '#C6A05C', illustration: 'M0,50 Q150,35 300,50 T600,40 L800,55 L800,80 L0,80 Z' },
  { name: 'Meadow Bloom', gradient: 'linear-gradient(135deg, #E8F0D8 0%, #C4DB9A 40%, #98C468 100%)', textColor: '#3A5A2A', accentColor: '#5A8C6A', illustration: 'M0,45 C150,55 250,35 400,50 S600,40 800,50 L800,80 L0,80 Z' },
  { name: 'Forest Canopy', gradient: 'linear-gradient(135deg, #D0DBC8 0%, #97B58A 40%, #5A8C5A 100%)', textColor: '#2A4A2A', accentColor: '#3D6A3D', illustration: 'M0,30 Q200,50 400,25 T800,40 L800,80 L0,80 Z' },
  { name: 'Winter Frost', gradient: 'linear-gradient(135deg, #EEF2F6 0%, #D0DCE8 40%, #A8BCC8 100%)', textColor: '#3A4A5A', accentColor: '#6890A8', illustration: 'M0,55 C200,45 400,60 600,42 L800,55 L800,80 L0,80 Z' },
  { name: 'Tropical Sunset', gradient: 'linear-gradient(135deg, #F5E0D0 0%, #E8B8A0 40%, #D08060 100%)', textColor: '#5A3020', accentColor: '#C06040', illustration: 'M0,40 Q200,55 400,35 T800,45 L800,80 L0,80 Z' },
];

export function NatureFooter() {
  const weekOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
  const theme = NATURE_THEMES[weekOfYear % NATURE_THEMES.length];
  return (
    <footer className="relative overflow-hidden" data-testid="nature-footer">
      <div className="absolute inset-0" style={{ background: theme.gradient, opacity: 0.4 }} />
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 800 80" preserveAspectRatio="none" style={{ height: 60, opacity: 0.08 }}>
        <path d={theme.illustration} fill={theme.accentColor} />
      </svg>
      <div className="relative py-10 px-6 md:px-12" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/nuri-logo-mark.svg" alt="nuri" style={{ height: 30 }} />
            </div>
            <div className="flex items-center gap-6 text-xs" style={{ color: C.muted }}>
              <a href="/privacy" style={{ color: C.muted, textDecoration: 'none' }}>Privacy Policy</a>
              <a href="/terms" style={{ color: C.muted, textDecoration: 'none' }}>Terms of Service</a>
              <span>Support</span>
              <span>Blog</span>
            </div>
            <div className="flex items-center gap-4" data-testid="social-links">
              <a href="https://instagram.com/asknuri" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="transition-transform hover:scale-110" style={{ color: C.muted }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="5"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </a>
              <a href="https://tiktok.com/@asknuri" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="transition-transform hover:scale-110" style={{ color: C.muted }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.11v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.42V13.3a8.16 8.16 0 004.22 1.17h.01V11.1a4.83 4.83 0 01-1.35.2 4.82 4.82 0 01-2.7-.83V2h3.4v.05a4.84 4.84 0 004 4.64z"/></svg>
              </a>
              <a href="https://x.com/asknuri" target="_blank" rel="noopener noreferrer" aria-label="X" className="transition-transform hover:scale-110" style={{ color: C.muted }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-[10px]" style={{ color: C.muted }}>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> HIPAA-mindful</span><span>|</span><span>Nuri AI Vision</span><span>|</span><span>Free to start</span>
          </div>
          <div className="mt-4 text-center"><Disclaimer /></div>
          <p className="mt-3 text-center text-[9px]" style={{ color: `${theme.accentColor}80` }}>{theme.name}</p>
        </div>
      </div>
    </footer>
  );
}
